/**
 * Date helpers. All report date ranges are interpreted in IST (+05:30),
 * matching the business timezone — v1 used UTC midnight, silently dropping
 * the first 5.5 hours of each selected day.
 *
 * The India-time helpers below compute from the epoch and fixed offset, so
 * the result does not depend on the browser's time zone (India has no
 * daylight saving time, so the offset never changes).
 */

const IST_OFFSET = '+05:30'

/** India time is Coordinated Universal Time plus 5 hours 30 minutes. */
export const IST_OFFSET_SECONDS = 19800

const DAY_SECONDS = 86400
const ISO_DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/
const DATETIME_LOCAL_RE = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/

/** 'YYYY-MM-DD' → Unix seconds at IST midnight (start of day). */
export const istDayStart = (isoDate) =>
    Math.floor(new Date(`${isoDate}T00:00:00${IST_OFFSET}`).getTime() / 1000)

/** 'YYYY-MM-DD' → Unix seconds at IST 23:59:59 (end of day). */
export const istDayEnd = (isoDate) =>
    Math.floor(new Date(`${isoDate}T23:59:59${IST_OFFSET}`).getTime() / 1000)

/** Current time as Unix seconds. */
export const nowUnix = () => Math.floor(Date.now() / 1000)

/**
 * The India-time calendar fields of an epoch second, as zero-padded strings.
 *
 * @param {number|string} epoch  Unix seconds
 * @returns {{year, month, day, hour, minute, second}|null} null when the
 *     value is not a finite number
 */
const istParts = (epoch) => {
    const seconds = Number(epoch)
    if (epoch === null || epoch === '' || !Number.isFinite(seconds)) return null
    const iso = new Date((seconds + IST_OFFSET_SECONDS) * 1000).toISOString()
    return {
        year: iso.slice(0, 4),
        month: iso.slice(5, 7),
        day: iso.slice(8, 10),
        hour: iso.slice(11, 13),
        minute: iso.slice(14, 16),
        second: iso.slice(17, 19),
    }
}

/** 'YYYY-MM-DD' in India time for an epoch second, or '' for no value. */
export const istIsoDate = (epoch) => {
    const p = istParts(epoch)
    return p ? `${p.year}-${p.month}-${p.day}` : ''
}

/** 'HH:mm' in India time for an epoch second, or '' for no value. */
export const istTime = (epoch) => {
    const p = istParts(epoch)
    return p ? `${p.hour}:${p.minute}` : ''
}

/**
 * 'DD-MM-YYYY HH:mm' in India time, the format the old comms app showed in
 * its report table; '' for no value.
 */
export const istDateTime = (epoch) => {
    const p = istParts(epoch)
    return p ? `${p.day}-${p.month}-${p.year} ${p.hour}:${p.minute}` : ''
}

/**
 * 'DD/MM/YYYY HH:mm:ss' in India time, the format the server writes into a
 * large report file (reports_generation_service, "%d/%m/%Y %H:%M:%S" in
 * Asia/Kolkata), so a small and a large report read the same; '' for no value.
 */
export const istFileDateTime = (epoch) => {
    const p = istParts(epoch)
    return p
        ? `${p.day}/${p.month}/${p.year} ${p.hour}:${p.minute}:${p.second}`
        : ''
}

/**
 * The value a `datetime-local` input shows for an epoch second in India
 * time ('YYYY-MM-DDTHH:mm'), e.g. for its `max`; '' for no value.
 */
export const istDateTimeInput = (epoch) => {
    const p = istParts(epoch)
    return p ? `${p.year}-${p.month}-${p.day}T${p.hour}:${p.minute}` : ''
}

/**
 * A `datetime-local` value ('YYYY-MM-DDTHH:mm') read as India time, to Unix
 * seconds; null when it is empty or not a real date and time.
 *
 * @param {string} value
 * @returns {number|null}
 */
export const istDateTimeEpoch = (value) => {
    const m = DATETIME_LOCAL_RE.exec(value || '')
    if (!m) return null
    const [y, mo, d, h, mi] = m.slice(1).map(Number)
    if (h > 23 || mi > 59) return null
    const utc = Date.UTC(y, mo - 1, d, h, mi)
    const check = new Date(utc)
    if (check.getUTCMonth() !== mo - 1 || check.getUTCDate() !== d) return null
    return utc / 1000 - IST_OFFSET_SECONDS
}

/**
 * The calendar date `days` after `isoDate` ('YYYY-MM-DD'); negative goes
 * back. Calendar arithmetic, so it never slips a day at a time-zone edge.
 */
export const isoAddDays = (isoDate, days) => {
    const m = ISO_DATE_RE.exec(isoDate || '')
    if (!m) return ''
    const [y, mo, d] = m.slice(1).map(Number)
    return new Date(Date.UTC(y, mo - 1, d) + days * DAY_SECONDS * 1000)
        .toISOString()
        .slice(0, 10)
}
